import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent } from "@aurumiq/ui/components/card";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { PageHeader } from "@/app/page-header";
import { SelectField, TextField } from "@/components/field";
import { useCreateOrg, usePlans } from "@/features/orgs/queries";
import { COUNTRIES } from "@/lib/countries";

const schema = z.object({
  name: z.string().trim().min(2, "Name is required"),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{0,12}$/, "Letters and digits only, up to 12"),
  country: z.string(),
  plan_code: z.string().min(1, "Choose a plan"),
  seats: z.coerce.number().int().min(1, "At least one seat"),
  trial_days: z.coerce.number().int().min(0, "0 or more").max(365, "At most 365"),
});
type FormValues = z.input<typeof schema>;
type Parsed = z.output<typeof schema>;

export function OrgNewPage() {
  const navigate = useNavigate();
  const plans = usePlans();
  const create = useCreateOrg();
  const form = useForm<FormValues, unknown, Parsed>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      code: "",
      country: "AE",
      plan_code: "starter",
      seats: 2,
      trial_days: 14,
    },
  });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;

  const onSubmit = form.handleSubmit(async (v) => {
    const org = await create.mutateAsync({
      name: v.name,
      code: v.code || undefined,
      country: v.country || undefined,
      plan_code: v.plan_code,
      seats: v.seats,
      trial_days: v.trial_days,
    });
    navigate(`/orgs/${org.id}`);
  });

  return (
    <>
      <PageHeader
        title="New organisation"
        description="Creates the tenant, its first subscription and a trial window."
      />
      <Card className="max-w-2xl">
        <CardContent>
          <form onSubmit={onSubmit} className="grid gap-3">
            <TextField label="Name" autoFocus error={err("name")} {...form.register("name")} />
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Code"
                placeholder="auto"
                hint="Optional; generated from the name when blank."
                className="font-mono"
                error={err("code")}
                {...form.register("code")}
              />
              <SelectField label="Country" {...form.register("country")}>
                <option value="">—</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} · {c.name}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Plan"
                error={err("plan_code")}
                hint={
                  plans.data?.fallback
                    ? "Billing plans endpoint unavailable; codes only."
                    : undefined
                }
                {...form.register("plan_code")}
              >
                {(plans.data?.items ?? []).map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </SelectField>
              <TextField
                label="Seats"
                type="number"
                min={1}
                error={err("seats")}
                {...form.register("seats")}
              />
              <TextField
                label="Trial days"
                type="number"
                min={0}
                max={365}
                error={err("trial_days")}
                {...form.register("trial_days")}
              />
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button asChild variant="outline">
                <Link to="/orgs">Cancel</Link>
              </Button>
              <Button type="submit" variant="gold" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create organisation"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
